import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, Users } from 'lucide-react';
import { useProviders } from '@/hooks/useProviders';
import { ProviderCard } from '@/components/listings/ProviderCard';
import { SectionWrapper } from '@/components/common';
import type { ServiceType } from '@/types';

interface FeaturedProvidersProps {
  title?: string;
  subtitle?: string;
  serviceType?: ServiceType;
  limit?: number;
  color?: string;
  bgColor?: string;
  viewAllLink?: string;
  className?: string;
}

export function FeaturedProviders({
  title,
  subtitle,
  serviceType,
  limit = 6,
  color = 'text-blue-600',
  bgColor = 'bg-blue-600',
  viewAllLink,
  className,
}: FeaturedProvidersProps) {
  const { t } = useTranslation(['services', 'common']);

  // Fetch providers for this service type
  const { data: providers, isLoading } = useProviders(
    serviceType ? { serviceTypes: [serviceType] } : {}
  );

  const displayTitle = title || (serviceType ? t(`services:${serviceType}.featured.title`) : 'Featured Providers');
  const displaySubtitle = subtitle || (serviceType ? t(`services:${serviceType}.featured.subtitle`) : '');
  const displayViewAllLink = viewAllLink || (serviceType ? `/search?type=${serviceType}` : '/search');

  return (
    <SectionWrapper bgColor="bg-white" className={className}>
      <div className="flex items-center justify-between mb-8">
        <div>
          {displayTitle && (
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
              {displayTitle}
            </h2>
          )}
          {displaySubtitle && (
            <p className="text-lg text-gray-600">
              {displaySubtitle}
            </p>
          )}
        </div>
        <Link
          to={displayViewAllLink}
          className={`hidden md:inline-flex items-center gap-2 ${color} font-medium hover:underline`}
        >
          {t('common:viewAll')}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {isLoading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse bg-gray-100 rounded-xl h-64" />
          ))}
        </div>
      ) : providers && providers.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {providers.slice(0, limit).map((provider) => (
            <ProviderCard key={provider.id} provider={provider} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('services:noProvidersYet')}
          </h3>
          <p className="text-gray-600 mb-4">
            {t('services:beFirstProvider')}
          </p>
          <Link to="/signup" className={`btn ${bgColor} text-white`}>
            {t('common:listYourBusiness')}
          </Link>
        </div>
      )}

      <div className="md:hidden mt-6 text-center">
        <Link
          to={displayViewAllLink}
          className={`inline-flex items-center gap-2 ${color} font-medium`}
        >
          {t('common:viewAll')}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </SectionWrapper>
  );
}
